import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  gap: 20px;
  padding: 0 10px 0 0;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

export const Title = styled.h2`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #303030;
`

export const Button = styled.button<{ variant?: 'primary' | 'outline' | 'danger' | 'danger-outline' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background-color: #0090D3;
          color: #FFFFFF;
          border-color: #0090D3;
        `;
      case 'danger':
        return `
          background-color: #F87171;
          color: #FFFFFF;
          border-color: #F87171;
        `;
      case 'danger-outline':
        return `
          background-color: #FFFFFF;
          color: #F87171;
        border-color: #F87171;
        `;
      case 'outline':
      default:
        return `
          background-color: #FFFFFF;
          color: #0090D3;
          border-color: #0090D3;
        `;
    }
  }}
`;

export const Card = styled.div`
  background-color: #FFFFFF;
  border-radius: 16px;
  border-left: 1px solid #f4f4f4;
  padding: 20px;
  margin-bottom: 20px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

export const SubTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #303030;
  margin-bottom: 16px;
`

export const FormGrid = styled.div<{ columns?: number }>`
  display: ${props => props.columns ? 'grid' : 'flex'};
  ${props => props.columns ? `grid-template-columns: repeat(${props.columns}, 1fr);` : ''}
  gap: 32px;
  margin-bottom: 20px;
  
  &.flex {
    display: flex;
    grid-template-columns: none;
  }
`

export const FormGroup = styled.div<{ hasIcon?: boolean; width?: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;

  ${props => props.width && `
    width: ${props.width};
    flex: none;
  `}

  ${props => props.hasIcon && `
    & > svg {
      position: absolute;
      right: 16px;
      bottom: 12px;
      color: #696969;
      pointer-events: none;
      z-index: 1;
    }

    & > input {
      padding-right: 48px;
    }
  `}
`

export const Label = styled.label`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  color: #303030;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const Select = styled.select<{ hasError?: boolean }>`
  padding: 10px 16px;
  padding-right: 40px;
  background-color: #EBEBEB;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #696969;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  appearance: none; 
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;

  ${props => props.hasError && `
    border: 1.5px solid #F87171;
    box-shadow: 0 0 3px #F87171;
  `}
`;

export const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 16px;
  background-color: #EBEBEB;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #696969;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  ${props => props.hasError && `
    border: 1.5px solid #F87171;
    box-shadow: 0 0 3px #F87171;
  `}

  &[readonly] {
    background-color:rgb(164, 164, 164);
  }
`;

export const AddNewItemButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`

export const AddProductButton = styled.button<{ variant?: 'primary' | 'outline', hasError?: boolean }>`
  width: fit-content;
  padding: .5rem 1rem;
  outline: none;
  border-radius: 2rem;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  margin: 0 auto;
  cursor: pointer;
  border: 1px solid;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  ${({ variant }) => {
    switch (variant) {
      case 'outline':
        return `
          color: #0090D3;
          background-color: #fff;
          border-color: #0090D3;
        `;
      case 'primary':
      default:
        return `
          color: #fff;
          background-color: #0090D3;
          border-color: #0090D3;
        `;
    }
  }}

  ${props => props.hasError && `
    border-color: #F87171;
    box-shadow: 0 0 3px #F87171;
  `}
`

export const Separator = styled.hr<{ margin?: string }>`
  border: none;
  height: 1px;
  background-color: #EBEBEB;
  ${(props) => props.margin ? `margin: ${props.margin};` : 'margin: 20px 0;'}
`

export const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  margin-bottom: 16px;
`

export const CircularCollapseIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #DADADA;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 10;

  &:hover {
    border-color: #0090D3;
    background: #f8fafb;
  }

  svg {
    width: 16px;
    height: 16px;
    color: #666;
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: #0090D3;
  }
`;

export const ExpandedItemContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #f4f4f4;
  padding: 20px;
  margin: 10px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const StyledSelect = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 16px;
  padding-right: 40px; 
  background-color: #EBEBEB;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #696969;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  cursor: pointer;
  appearance: none; 
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;

  &:hover {
    border-color: #cbd5e0;
  }

  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
  }

  &::placeholder {
    color: #696969;
    font-style: italic;
  }

  ${props => props.hasError && `
    border: 1.5px solid #F87171;
    box-shadow: 0 0 3px #F87171;
  `}
`; 